import type { FC, ReactNode } from 'react'

import type {
    Catalog,
    CatalogProduct,
    CatalogStatus,
    Category,
    Movement,
    MovementType,
    Product
} from '@/types'

import type { DeleteTarget } from '@/components/inventory/delete-product-dialog'

import { toast } from 'sonner'
import { useRouter } from 'next/router'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react'

import { apiRequest } from '@/lib/api'
import { useRefresh } from '@/hooks/use-refresh'
import { MOVEMENT_TYPE_META } from '@/constants/inventory'
import { useSettings } from '@/components/generals/settings-provider'
import MovementDialog from '@/components/inventory/movement-dialog'
import ProductFormDialog from '@/components/inventory/product-form-dialog'
import DeleteProductDialog from '@/components/inventory/delete-product-dialog'

type MovementOptions = {
    product?: CatalogProduct | null
    type?: MovementType
}

type DeleteOptions = {
    redirectTo?: string
}

type InventoryActions = {
    openNewProduct: () => void
    openEditProduct: (product: Product) => void
    openMovement: (options?: MovementOptions) => void
    openDeleteProduct: (product: DeleteTarget, options?: DeleteOptions) => void
}

type MovementState = {
    open: boolean
    key: number
    type: MovementType
    product: CatalogProduct | null
}

type ProductFormState = {
    open: boolean
    key: number
    product: Product | null
}

type DeletionState = {
    open: boolean
    product: DeleteTarget | null
    redirectTo?: string
}

interface InventoryActionsProviderProps {
    children: ReactNode
}

const InventoryActionsContext = createContext<InventoryActions | null>(null)

export const toCatalogProduct = (product: Product | CatalogProduct): CatalogProduct => ({
    id: product.id,
    name: product.name,
    sku: product.sku,
    unit: product.unit,
    quantity: product.quantity,
    minStock: product.minStock
})

export const InventoryActionsProvider: FC<InventoryActionsProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const refresh = useRefresh()
    const { number } = useSettings()
    const catalogRequest = useRef(0)

    const [catalog, setCatalog] = useState<Catalog | null>(null)
    const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>('idle')
    const [movement, setMovement] = useState<MovementState>({ open: false, key: 0, type: 'entrada', product: null })
    const [productForm, setProductForm] = useState<ProductFormState>({ open: false, key: 0, product: null })
    const [deletion, setDeletion] = useState<DeletionState>({ open: false, product: null })

    const loadCatalog = useCallback(async () => {
        const request = ++catalogRequest.current

        setCatalogStatus('loading')

        try {
            const data = await apiRequest<Catalog>('/api/catalog')

            if (request !== catalogRequest.current) return

            setCatalog(data)
            setCatalogStatus('ready')
        }
        catch {
            if (request !== catalogRequest.current) return

            setCatalogStatus('error')
        }
    }, [])

    const openMovement = useCallback((options: MovementOptions = {}) => {
        setMovement(previous => ({
            open: true,
            key: previous.key + 1,
            type: options.type ?? 'entrada',
            product: options.product ?? null
        }))

        void loadCatalog()
    }, [loadCatalog])

    const openNewProduct = useCallback(() => {
        setProductForm(previous => ({ open: true, key: previous.key + 1, product: null }))

        void loadCatalog()
    }, [loadCatalog])

    const openEditProduct = useCallback((product: Product) => {
        setProductForm(previous => ({ open: true, key: previous.key + 1, product }))

        void loadCatalog()
    }, [loadCatalog])

    const openDeleteProduct = useCallback((product: DeleteTarget, options: DeleteOptions = {}) => {
        setDeletion({ open: true, product, redirectTo: options.redirectTo })
    }, [])

    const handleMovementSaved = useCallback(async (saved: Movement) => {
        setMovement(previous => ({ ...previous, open: false }))

        const stock = `${number(saved.stockAfter ?? 0)} ${saved.productUnit}`
        const change = `${saved.quantityChange > 0 ? '+' : '−'}${number(Math.abs(saved.quantityChange))} ${saved.productUnit}`

        toast.success(MOVEMENT_TYPE_META[saved.type].successLabel, {
            description: saved.type === 'ajuste'
                ? `${saved.productName}: el stock queda en ${stock}.`
                : `${saved.productName}: ${change}. Stock actual: ${stock}.`
        })

        await refresh()
    }, [number, refresh])

    const handleProductSaved = useCallback(async (saved: Product, mode: 'create' | 'update') => {
        setProductForm(previous => ({ ...previous, open: false }))

        toast.success(mode === 'create' ? 'Producto creado' : 'Cambios guardados', { description: saved.name })

        await refresh()
    }, [refresh])

    const handleCategoryCreated = useCallback((category: Category) => {
        setCatalog(previous => {
            const categories = [...(previous?.categories ?? []), category]
                .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))

            return { products: previous?.products ?? [], categories }
        })
    }, [])

    const handleDeleted = useCallback(async (product: DeleteTarget) => {
        const { redirectTo } = deletion

        setDeletion(previous => ({ ...previous, open: false }))
        toast.success('Producto eliminado', { description: product.name })

        if (redirectTo) await router.push(redirectTo)
        else await refresh()
    }, [deletion, refresh, router])

    const value = useMemo<InventoryActions>(() => ({
        openNewProduct,
        openEditProduct,
        openMovement,
        openDeleteProduct
    }), [openNewProduct, openEditProduct, openMovement, openDeleteProduct])

    return (
        <InventoryActionsContext.Provider value={value}>
            {children}

            <MovementDialog
                open={movement.open}
                onOpenChange={open => setMovement(previous => ({ ...previous, open }))}
                requestKey={movement.key}
                initialType={movement.type}
                initialProduct={movement.product}
                catalog={catalog}
                catalogStatus={catalogStatus}
                onReloadCatalog={loadCatalog}
                onSaved={handleMovementSaved}
            />

            <ProductFormDialog
                open={productForm.open}
                onOpenChange={open => setProductForm(previous => ({ ...previous, open }))}
                requestKey={productForm.key}
                product={productForm.product}
                categories={catalog?.categories ?? []}
                catalogStatus={catalogStatus}
                onCategoryCreated={handleCategoryCreated}
                onSaved={handleProductSaved}
            />

            <DeleteProductDialog
                open={deletion.open}
                onOpenChange={open => setDeletion(previous => ({ ...previous, open }))}
                product={deletion.product}
                onDeleted={handleDeleted}
            />
        </InventoryActionsContext.Provider>
    )
}

export const useInventoryActions = (): InventoryActions => {
    const context = useContext(InventoryActionsContext)

    if (!context) throw new Error('useInventoryActions must be used inside InventoryActionsProvider')

    return context
}
